import { EventOrigin } from "@/interfaces/EventOrigin";
import { Event } from "@/interfaces/Event";
import { Incident } from "@/interfaces/Incident";
import { Sensor } from "@/interfaces/Sensor";
import { Data } from "@/interfaces/Data";
import { Burst, SingleBurst } from "@/interfaces/Burst";
import { ConditionMatchedPeriod } from "@/interfaces/ConditionMatchedPeriod";
import { Aggregator } from "@/interfaces/Aggregator";
import { Task } from "@/interfaces/Task";
import { TaskRule } from "@/interfaces/TaskRule";
import { TaskRun } from "@/interfaces/TaskRun";
import {
  Sites,
  Companies,
  Customers,
  TurbineModel,
  Node,
  Turbine,
  Blade,
  BladeComponent,
  BladeDamageType,
  MetricType,
  Metric,
  BladeResponseRecord,
  BladeHealthRecord,
  SystemAggregators,
  BladeModels,
  NewTurbineForm,
  NewCustomerForm,
  NewAggregatorForm,
  NewTurbineModelForm,
} from "@/interfaces/systemsdb";

import {
  Threshold,
  LocalThreshold,
  AlertHistoryResponse,
  toApiThreshold,
  toLocalThreshold,
} from "@/interfaces/Threshold";
import { BalenaDevice } from "@/interfaces/Balena";
import { Temporal } from "@js-temporal/polyfill";
import { ServiceConfiguration } from "@/interfaces/ServiceConfiguration";
import { GrafanaPreferences } from "@/interfaces/GrafanaPreferences";
import { clearAuthToken, getAuthToken, getBalenaAuthToken } from "./store";
import { InvalidCredentialsError, PermissionsError, SessionExpiredError, TimeoutError } from "@/errors/errors";
import prettifyJSON from "@/components/PrettifyJSON.vue";

// Error handling helper function which checks whether the response is json before trying to read it as json. Avoids
// broken seeming error messages when the server 500s.
async function handleResponseError(response: Response) {
  const contentType = response.headers.get("content-type");
  let message = `HTTP ${response.status}`;
  if (contentType?.includes("application/json")) {
    const err = await response.json();
    message = err.message ?? err.error ?? JSON.stringify(err);
  } else {
    message = await response.text();
  }
  throw new Error(message);
}

const fetchWithToken = async (location: string, init: RequestInit = { headers: {} }): Promise<Response> => {
  const initWithAuth = {
    ...init,
    headers: { ...init.headers, Authorization: `Bearer ${getAuthToken()}` },
  };
  const response = await fetch(location, initWithAuth);
  if (response.status === 401) {
    // Drop the rejected token so the next page load doesn't reuse it, and throw so no
    // caller can read the 401 body as data.
    clearAuthToken();
    if (window.location.pathname != "/login") {
      window.location.assign("/login");
    }
    throw new SessionExpiredError("Your session has expired - please log in again");
  } else if (response.status === 403) {
    //Tell them they don't have permission and stop operation
    throw new PermissionsError("You do not have permission to perform that operation");
  }
  return response;
};

// GET-and-parse for JSON endpoints. Error statuses throw with the API's message instead of
// handing callers an {"error": ...} body in place of the data they expect.
const fetchJsonWithToken = async (location: string, init?: RequestInit) => {
  const response = await fetchWithToken(location, init);
  if (!response.ok) {
    await handleResponseError(response);
  }
  return response.json();
};

const fetchWithBalenaToken = async (location: string, init: RequestInit = { headers: {} }): Promise<Response> => {
  const appToken = getAuthToken();
  const balenaToken = getBalenaAuthToken(); // from auth/balena/login
  const headers = { ...(init.headers ?? {}) } as Record<string, string>;

  // Keep your normal API auth (so your backend authorizes the call)
  if (appToken) {
    headers["Authorization"] = `Bearer ${appToken}`;
  }

  // Send Balena token in a separate header for the backend to use
  if (balenaToken) {
    headers["X-Balena-Authorization"] = `Bearer ${balenaToken}`;
  }

  const response = await fetch(location, { ...init, headers });

  // If your backend uses 401 to indicate "balena token missing/expired", propagate that upward
  if (response.status === 403) {
    throw new PermissionsError("You do not have permission to perform that operation");
  }

  return response;
};

const fetchOrigins = async (): Promise<EventOrigin[]> => {
  return fetchJsonWithToken("/api/v1/event_origins");
};

const fetchEvents = async (params: URLSearchParams): Promise<Event[]> => {
  return fetchJsonWithToken(`/api/v1/events?${params.toString()}`);
};

const fetchIncidents = async (params: URLSearchParams): Promise<Incident[]> => {
  return fetchJsonWithToken(`/api/v1/incidents?${params.toString()}`);
};

const fetchSensors = async (): Promise<Sensor[]> => {
  return fetchJsonWithToken("/api/v1/sensors");
};

const fetchData = async (start_time: Temporal.Instant): Promise<Data[]> => {
  return fetchJsonWithToken(`/api/v1/data/data?Limit=1000&StartTime=${start_time.epochSeconds}`);
  // NB: at the moment the API will return the most recent 1000 bursts and the earliest 1000 data samples in the specified period!
};

const fetchBursts = async (params: URLSearchParams): Promise<Burst[]> => {
  return fetchJsonWithToken(`/api/v1/data/bursts?${params.toString()}`);
};

const fetchSimilarPeriods = async (params: URLSearchParams): Promise<ConditionMatchedPeriod[]> => {
  const withSortedArrays = new URLSearchParams(params);
  withSortedArrays.set("sorted_arrays", "true");
  return fetchJsonWithToken(`/api/v1/data/get_similar_periods?${withSortedArrays.toString()}`);
};

const fetchAggregators = async (): Promise<Aggregator[]> => {
  return fetchJsonWithToken("/api/v1/aggregators");
};

const fetchBurst = async (burstId: string): Promise<SingleBurst> => {
  return fetchJsonWithToken(`/api/v1/data/burst/${burstId}`);
};

// The available-task list only changes when a worker registers a new task type (a deploy), so it's
// effectively static within a session. Cache the in-flight promise so repeated callers (e.g. reopening
// the task-rule modal) reuse one request instead of re-hitting the API each time. Cleared on full reload;
// pass forceRefresh to bypass. Failures aren't cached so a later call can retry.
let tasksCache: Promise<Task[]> | null = null;

const fetchTasks = (forceRefresh = false): Promise<Task[]> => {
  if (!tasksCache || forceRefresh) {
    tasksCache = fetchJsonWithToken("/api/v1/tasks").catch((err) => {
      tasksCache = null;
      throw err;
    });
  }
  return tasksCache;
};

const fetchTaskRules = async (): Promise<TaskRule[]> => {
  return fetchJsonWithToken("/api/v1/task_rules");
};

const fetchTaskRuns = async (params: URLSearchParams): Promise<TaskRun[]> => {
  return fetchJsonWithToken(`/api/v1/task_runs?${params.toString()}`);
};

// The task-run detail endpoint can hang if the API's connection pool is saturated or its event loop is
// stalled, and without a timeout the logs modal spins forever. Abort after a bounded wait so the caller
// always settles and can surface an error instead.
const TASK_RUN_DETAIL_TIMEOUT_MS = 20000;

const fetchTaskRun = async (taskRunId: number): Promise<TaskRun> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TASK_RUN_DETAIL_TIMEOUT_MS);
  try {
    return await fetchJsonWithToken(`/api/v1/task_runs/${taskRunId}`, { signal: controller.signal });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new TimeoutError("Timed out loading logs");
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
};

const fetchTaskRunCounts = async (params: URLSearchParams): Promise<Record<number, number>> => {
  return fetchJsonWithToken(`/api/v1/count_task_runs_by_rule?${params.toString()}`);
};

const fetchThresholds = async (): Promise<Threshold[]> => {
  return fetchJsonWithToken(`/api/v1/thresholds`);
};

const fetchAlertHistory = async (thresholdId: number, from?: number, to?: number): Promise<AlertHistoryResponse> => {
  const params = new URLSearchParams();
  if (from !== undefined) params.set("from", from.toString());
  if (to !== undefined) params.set("to", to.toString());
  const query = params.toString() ? `?${params.toString()}` : "";
  return fetchJsonWithToken(`/api/v1/thresholds/${thresholdId}/alert-history${query}`);
};

const fetchSensorTagOptions = async (): Promise<Record<string, string[]>> => {
  return fetchJsonWithToken(`/api/v1/sensor_tag_options`);
};

const fetchSensorNames = async (params: URLSearchParams): Promise<string[]> => {
  return fetchJsonWithToken(`/api/v1/sensors/names?${params.toString()}`);
};

const fetchBalenaDevices = async (): Promise<BalenaDevice[]> => {
  const res = await fetchWithBalenaToken("/api/v1/balena/devices");
  if (res.status === 401) {
    throw new InvalidCredentialsError("Balena login required");
  }
  if (!res.ok) {
    await handleResponseError(res);
  }
  return res.json();
};

const fetchBalenaFleets = async (): Promise<string[]> => {
  return fetchJsonWithToken("/auth/balena/fleets");
};

// Returns the TOML file (not base64 encoded)
const fetchBalenaAggregatorConfig = async (deviceUUID: string): Promise<string> => {
  const res = await fetchWithBalenaToken(`/api/v1/balena/device/${deviceUUID}/config.toml`);
  if (res.status === 401) {
    throw new InvalidCredentialsError("Balena login required");
  }
  return res.text();
};

// POSTs config file, currently in TOML format.  Do not base-64 encode it.
const updateBalenaAggregatorConfig = async (deviceUUID: string, conf: string): Promise<void> => {
  const res = await fetchWithBalenaToken(`/api/v1/balena/device/${deviceUUID}/config.toml`, {
    method: "POST",
    body: conf,
    headers: {
      "Content-Type": "application/toml",
    },
  });
  if (res.status === 401) {
    throw new InvalidCredentialsError("Balena login required");
  }
};

const fetchServiceConfiguration = async (): Promise<ServiceConfiguration> => {
  return fetchJsonWithToken("/api/v1/service_configuration");
};

const fetchGrafanaPreferences = async (): Promise<GrafanaPreferences> => {
  const grafanaPreferences = await fetch("/grafana/api/user/preferences");
  return grafanaPreferences.json();
};

const postRerunTask = async (taskID: number): Promise<void> => {
  await fetchWithToken(`/api/v1/rerun_task/${taskID}`, {
    method: "POST",
  });
};

// The write endpoints return HTTP 204 (empty) on plain success for updates, and HTTP 200
// {"task_rule_id": N, "warnings"?: [...]} for new-rule creates (which always carry the id)
// and for any write with warnings. Normalize into one shape so the modal can surface
// warnings and capture the id without having to distinguish HTTP status codes.
type TaskRuleWriteResult = { task_rule_id: number | null; warnings: string[] } | null;

const parseTaskRuleWriteResponse = async (response: Response): Promise<TaskRuleWriteResult> => {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    const body = JSON.parse(text) as { task_rule_id?: unknown; warnings?: unknown };
    const taskRuleID = typeof body?.task_rule_id === "number" ? body.task_rule_id : null;
    const warnings = Array.isArray(body?.warnings) ? (body.warnings as unknown[]).map(String) : [];
    if (taskRuleID === null && warnings.length === 0) return null;
    return { task_rule_id: taskRuleID, warnings };
  } catch {
    return null;
  }
};

const postNewTask = async (rule: TaskRule): Promise<TaskRuleWriteResult> => {
  const response = await fetchWithToken("/api/v1/new_task_rule", {
    method: "POST",
    body: JSON.stringify(rule),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
  return parseTaskRuleWriteResponse(response);
};

const postUpdateTask = async (rule: TaskRule, taskRuleID: number): Promise<TaskRuleWriteResult> => {
  const response = await fetchWithToken(`/api/v1/update_task_rule/${taskRuleID}`, {
    method: "POST",
    body: JSON.stringify(rule),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
  return parseTaskRuleWriteResponse(response);
};

const postNewThreshold = async (threshold: LocalThreshold): Promise<LocalThreshold> => {
  const response = await fetchWithToken("/api/v1/thresholds", {
    method: "POST",
    body: JSON.stringify(toApiThreshold(threshold)),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
  return toLocalThreshold(await response.json());
};

const putUpdateThreshold = async (threshold: LocalThreshold): Promise<LocalThreshold> => {
  const response = await fetchWithToken(`/api/v1/thresholds/${threshold.ThresholdID}`, {
    method: "PUT",
    body: JSON.stringify(toApiThreshold(threshold)),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
  return toLocalThreshold(await response.json());
};

const deleteThreshold = async (thresholdId: number): Promise<Response> => {
  const response = await fetchWithToken(`/api/v1/thresholds/${thresholdId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
  return response;
};

const putUpdateGrafanaPreferences = async (preferences: GrafanaPreferences): Promise<Response> => {
  const response = await fetch("/grafana/api/user/preferences", {
    method: "PUT",
    body: JSON.stringify(preferences),
    headers: {
      "Content-Type": "application/json",
    },
  });
  return response.json();
};

interface AuthResponse extends Response {
  Message: string;
  AuthToken: string;
}

/*
The following are for the systems db
*/

const fetchNodes = async (): Promise<Node[]> => {
  return fetchJsonWithToken("/api/v1/data/nodes?limit=1000");
};

const patchNode = async (idHex: string, aggregatorName: string | null, bladeName: string | null): Promise<void> => {
  await fetchWithToken(`/api/v1/data/nodes/${idHex}/assign`, {
    method: "PATCH",
    body: JSON.stringify({ aggregator_name: aggregatorName, blade_name: bladeName }),
    headers: { "Content-Type": "application/json" },
  });
};

const fetchSites = async (country?: string, hasTurbines?: boolean, limit?: number): Promise<Sites[]> => {
  const params = new URLSearchParams();

  if (country) {
    params.set("country", country);
  }

  if (hasTurbines) {
    params.set("has_turbines", "true");
  }

  if (limit !== undefined) {
    params.set("Limit", String(limit));
  }

  const query = params.toString() ? `?${params.toString()}` : "";
  return fetchJsonWithToken(`/api/v1/data/list_sites${query}`);
};

const fetchManufacturingCompanies = async (): Promise<Companies[]> => {
  return fetchJsonWithToken("/api/v1/systems/list_manufacturing_companies");
};

const fetchCountries = async (): Promise<string[]> => {
  return fetchJsonWithToken("/api/v1/systems/list_countries");
};

const fetchSystemAggregators = async (): Promise<SystemAggregators[]> => {
  return fetchJsonWithToken(`/api/v1/systems/list_system_aggregators`);
};

const fetchCustomersNames = async (): Promise<string[]> => {
  return fetchJsonWithToken(`/api/v1/systems/list_customers_names`);
};

const fetchCustomers = async (name?: string): Promise<Customers[]> => {
  const params = new URLSearchParams();

  if (name) {
    params.set("name", name);
  }

  const query = params.toString() ? `?${params.toString()}` : "";
  return fetchJsonWithToken(`/api/v1/systems/list_customers${query}`);
};

const fetchTurbineModel = async (designer?: string): Promise<TurbineModel[]> => {
  const params = new URLSearchParams();

  if (designer) {
    params.set("designer", designer);
  }

  const query = params.toString() ? `?${params.toString()}` : "";
  return fetchJsonWithToken(`/api/v1/systems/list_turbine_models${query}`);
};

const fetchMetricTypes = async (): Promise<MetricType[]> => {
  return fetchJsonWithToken("/api/v1/systems/list_metric_types");
};

const fetchMetrics = async (metricTypeId?: number): Promise<Metric[]> => {
  const params = new URLSearchParams();
  if (metricTypeId !== undefined) params.set("metric_type_id", String(metricTypeId));
  const query = params.toString() ? `?${params.toString()}` : "";
  return fetchJsonWithToken(`/api/v1/systems/list_metrics${query}`);
};

const postBladeResponse = async (form: Record<string, unknown>): Promise<void> => {
  const response = await fetchWithToken("/api/v1/data/blade_response/new", {
    method: "POST",
    body: JSON.stringify({ form }),
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
};

const fetchBladeResponse = async (params: {
  site_id?: number | null;
  turbine_id?: number | null;
  blade_id?: number | null;
}): Promise<BladeResponseRecord[]> => {
  const q = new URLSearchParams();
  if (params.blade_id) q.set("blade_id", String(params.blade_id));
  else if (params.turbine_id) q.set("turbine_id", String(params.turbine_id));
  else if (params.site_id) q.set("site_id", String(params.site_id));
  return fetchJsonWithToken(`/api/v1/systems/list_blade_response?${q.toString()}`);
};

const fetchBladeHealth = async (params: {
  site_id?: number | null;
  turbine_id?: number | null;
  blade_id?: number | null;
  component_id?: number | null;
}): Promise<BladeHealthRecord[]> => {
  const q = new URLSearchParams();
  if (params.blade_id) q.set("blade_id", String(params.blade_id));
  else if (params.turbine_id) q.set("turbine_id", String(params.turbine_id));
  else if (params.site_id) q.set("site_id", String(params.site_id));
  if (params.component_id) q.set("component_id", String(params.component_id));
  return fetchJsonWithToken(`/api/v1/systems/list_blade_health?${q.toString()}`);
};

const postBladeDamage = async (form: Record<string, unknown>): Promise<void> => {
  const response = await fetchWithToken("/api/v1/data/blade_damage/new", {
    method: "POST",
    body: JSON.stringify({ form }),
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
};

const fetchBladeComponents = async (): Promise<BladeComponent[]> => {
  return fetchJsonWithToken("/api/v1/systems/list_blade_components");
};

const fetchBladeDamageTypes = async (componentId: number): Promise<BladeDamageType[]> => {
  const params = new URLSearchParams();
  params.set("component_id", String(componentId));
  return fetchJsonWithToken(`/api/v1/systems/list_blade_damage_types?${params.toString()}`);
};

const fetchBlades = async (turbineId: number): Promise<Blade[]> => {
  const params = new URLSearchParams();
  params.set("turbine_id", String(turbineId));
  return fetchJsonWithToken(`/api/v1/systems/list_blades?${params.toString()}`);
};

const fetchTurbines = async (siteId?: number): Promise<Turbine[]> => {
  const params = new URLSearchParams();

  if (siteId !== undefined) {
    params.set("site_id", String(siteId));
  }

  const query = params.toString() ? `?${params.toString()}` : "";
  return fetchJsonWithToken(`/api/v1/systems/list_turbines${query}`);
};

const fetchCommissioningReport = async (asset: string): Promise<string> => {
  const params = new URLSearchParams();
  params.set("asset", asset);

  const response = await fetchWithToken(`/api/v1/commissioning/report?${params.toString()}`);

  if (!response.ok) {
    await handleResponseError(response);
  }

  return response.text();
};

const fetchBladeManufacturersForGivenTurbine = async (turbineModel: string): Promise<string[]> => {
  const params = new URLSearchParams();
  params.set("turbine_model", turbineModel);

  return fetchJsonWithToken(`/api/v1/systems/list_blade_manufacturers_for_given_turbine?${params.toString()}`);
};

const fetchBladeModelsForGivenTurbine = async (turbineModel: string, manufacturer: string): Promise<BladeModels[]> => {
  const params = new URLSearchParams();
  params.set("turbine_model", turbineModel);
  params.set("manufacturer", manufacturer);

  return fetchJsonWithToken(`/api/v1/systems/list_blade_models_for_given_turbine?${params.toString()}`);
};

const postNewTurbine = async (form: NewTurbineForm): Promise<{ status: string; blade_ids: Record<string, number> }> => {
  console.log("postNewTurbine request body:", {
    form,
  });

  const response = await fetchWithToken("/api/v1/data/turbines/new", {
    method: "POST",
    body: JSON.stringify({
      form,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    await handleResponseError(response);
  }

  return response.json();
};

const postNewCustomer = async (form: NewCustomerForm): Promise<Response> => {
  console.log("postNewCustomer request body:", {
    form,
  });

  const response = await fetchWithToken("/api/v1/data/customer/new", {
    method: "POST",
    body: JSON.stringify({
      form,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    await handleResponseError(response);
  }

  return response.json();
};

const postAggregatorSerial = async (form: NewAggregatorForm): Promise<Response> => {
  console.log("PostAggregatorSerial request body:", {
    form,
  });

  const response = await fetchWithToken("/api/v1/data/agg_serial/new", {
    method: "POST",
    body: JSON.stringify({
      form,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    await handleResponseError(response);
  }

  return response.json();
};

const postNewTurbineModel = async (form: NewTurbineModelForm): Promise<void> => {
  const response = await fetchWithToken("/api/v1/data/turbine_model/new", {
    method: "POST",
    body: JSON.stringify({ form }),
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    await handleResponseError(response);
  }
};

const postNewBladeModel = async (form: NewTurbineModelForm): Promise<Response> => {
  console.log("postNewBladeModel request body:", {
    form,
  });

  const response = await fetchWithToken("/api/v1/data/blade_model/new", {
    method: "POST",
    body: JSON.stringify({
      form,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    await handleResponseError(response);
  }

  return response.json();
};

interface MeResponse {
  Username: string;
  Email: string;
  Roles: string[];
}

const fetchMe = async (): Promise<MeResponse> => {
  return fetchJsonWithToken("/auth/me");
};

const postLogin = async (username: string, password: string): Promise<AuthResponse> => {
  const response = await fetch("/auth/login", {
    method: "POST",
    body: JSON.stringify({
      Username: username,
      Password: password,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (response.status === 401) {
    throw new InvalidCredentialsError("Invalid Credentials");
  }
  return response.json();
};

const postBalenaLogin = async (token: string, fleet: string): Promise<AuthResponse> => {
  const response = await fetch("/auth/balena/login", {
    method: "POST",
    body: JSON.stringify({
      Token: token,
      Fleet: fleet,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (response.status === 401) {
    throw new InvalidCredentialsError("Invalid Credentials");
  }
  return response.json();
};

export {
  fetchAlertHistory,
  fetchEvents,
  fetchOrigins,
  fetchIncidents,
  fetchSensors,
  fetchSensorTagOptions,
  fetchSensorNames,
  fetchData,
  fetchBursts,
  fetchSimilarPeriods,
  fetchAggregators,
  fetchBurst,
  fetchTasks,
  fetchTaskRules,
  fetchTaskRuns,
  fetchTaskRun,
  fetchTaskRunCounts,
  fetchThresholds,
  fetchBalenaDevices,
  fetchBalenaAggregatorConfig,
  updateBalenaAggregatorConfig,
  fetchServiceConfiguration,
  fetchGrafanaPreferences,
  postRerunTask,
  postNewTask,
  postUpdateTask,
  postNewThreshold,
  putUpdateThreshold,
  deleteThreshold,
  putUpdateGrafanaPreferences,
  postLogin,
  fetchBalenaFleets,
  postBalenaLogin,
  fetchNodes,
  fetchSites,
  fetchTurbines,
  fetchCommissioningReport,
  fetchBlades,
  fetchBladeComponents,
  fetchBladeDamageTypes,
  postBladeDamage,
  fetchBladeHealth,
  fetchBladeResponse,
  fetchMetricTypes,
  fetchMetrics,
  postBladeResponse,
  fetchManufacturingCompanies,
  fetchTurbineModel,
  fetchBladeManufacturersForGivenTurbine,
  fetchBladeModelsForGivenTurbine,
  fetchCountries,
  fetchSystemAggregators,
  fetchCustomers,
  fetchCustomersNames,
  patchNode,
  postNewTurbine,
  postNewCustomer,
  postAggregatorSerial,
  postNewTurbineModel,
  postNewBladeModel,
  fetchMe,
};
